import { StatusCode } from '@yandex-video-platform/goloom-sdk';
import { Effect } from 'redux-saga/effects';
import { all, call, put, race, take } from 'typed-redux-saga';

import { resetDeviceManagerErrorSaga } from '../../../../../shared/features/device-manager/sagas';
import { deviceManager, getIsSdkError } from '../../../../../shared/features/goloom-sdk';
import { refreshMediaDevices } from '../../../../../shared/features/settings/sagas';
import { browser } from '../../../../../shared/helpers/browser-capabilities';
import { emitterEventChannelFactory } from '../../../../../shared/helpers/emitterEventChannelFactory';
import { createLogger } from '../../../../../shared/helpers/loggerFacade';
import { countMetrika } from '../../../../../shared/helpers/metrika';
import { DeviceAccessError } from '../../../types';
import { setCameraPermission, setMicrophonePermission } from '../slice';

const logger = createLogger('goloom:device-permissions');

/**
 * Следит за изменениями состояний доступов до устройств
 */
export function* watchDevicesPermissionsSaga(): Generator<Effect, void> {
    if (NODE || !navigator?.permissions?.query || browser()?.isFirefox() || browser()?.isHeadlessChrome()) {
        return;
    }

    logger.event('Start watch devices permissions', {
        additional: {
            metod: 'watchDevicesPermissionsSaga',
        },
    });

    try {
        const permissionsResults = yield* all([
            call([navigator.permissions, navigator.permissions.query], createPermissionDescriptor('camera')),
            call([navigator.permissions, navigator.permissions.query], createPermissionDescriptor('microphone')),
        ]);

        logger.event('Get current devices permissions', {
            additional: {
                metod: 'watchDevicesPermissionsSaga',
                cameraPermission: permissionsResults[0].state,
                microphonePermission: permissionsResults[1].state,
            },
        });

        yield* put(setCameraPermission(permissionsResults[0].state));
        yield* put(setMicrophonePermission(permissionsResults[1].state));

        const cameraPermissionsChangedChannel = emitterEventChannelFactory(permissionsResults[0], 'change');
        const microphonePermissionsChangedChannel = emitterEventChannelFactory(permissionsResults[1], 'change');

        while (true) {
            const [microphone, camera] = yield* race([
                take(microphonePermissionsChangedChannel),
                take(cameraPermissionsChangedChannel),
            ]);

            if (microphone) {
                logger.event('Microphone permission changed', {
                    additional: {
                        metod: 'watchDevicesPermissionsSaga',
                        microphonePermission: microphone[0],
                    },
                });
                yield* call(handlePermissionChange, false, microphone[0]);
            } else if (camera) {
                logger.event('Camera permission changed', {
                    additional: {
                        metod: 'watchDevicesPermissionsSaga',
                        cameraPermission: camera[0],
                    },
                });
                yield* call(handlePermissionChange, true, camera[0]);
            }
        }
    } catch (error) {
        yield* call(countMetrika, 'watch_devices_permissions', getDeviceAccessErrorFromError(error));

        logger.error(error, {
            method: 'watchDevicesPermissionsSaga',
        });
    }
}

/**
 * Типы TS не поддерживают camera и microphone поэтому нужна такая фабрика
 *
 * @param name название
 * @returns Дескприптор разрешений
 */
function createPermissionDescriptor(name: 'camera' | 'microphone'): PermissionDescriptor {
    return {
        name,
    } as unknown as PermissionDescriptor;
}

/**
 * Обработчик изменения разрешений к девайсам
 *
 * @param isVideo - видео или аудио
 * @param event - событие
 */
function* handlePermissionChange(isVideo: boolean, event: Event) {
    if (!event) {
        logger.event('Handle permission change without Event body', {
            additional: {
                metod: 'handlePermissionChange',
            },
        });
        return;
    }

    // @ts-expect-error - в типе Event нет свойства state
    const permissionState: PermissionState = event?.target?.state;

    const isGranted = permissionState === 'granted';

    const kind = isVideo ? 'videoinput' : 'audioinput';
    const error = StatusCode.DEVICE_DENIED_BY_USER;
    if (isVideo) {
        yield* put(setCameraPermission(permissionState));
    } else {
        yield* put(setMicrophonePermission(permissionState));
    }
    if (isGranted) {
        yield* call(resetDeviceManagerErrorSaga, { kind, error });
    }

    yield* call([deviceManager, deviceManager.syncMuteState], kind);
    yield* call(refreshMediaDevices);
}

function getDeviceAccessErrorFromError(error: Error | unknown): DeviceAccessError {
    if (getIsSdkError(error)) {
        return error.code === StatusCode.DEVICE_DENIED_BY_USER
            ? DeviceAccessError.PermissionDenied
            : DeviceAccessError.General;
    }

    return DeviceAccessError.General;
}
